/**
 * GLSL ES 3.00 port of the desktop DirectVolumeRenderingShader.shader (DVR / MIP / ISO), see
 * docs/dev-notes/volume-shader-port.md for what differs:
 *  - the gradient is a central difference computed in the shader (no gradient texture);
 *  - no SV_DEPTH output (the desktop never writes depth either, ZWrite 0);
 *  - the cusp-field leaflet terms are the desktop's v15 LEAFLET OVERLAY path (shell off, cusp
 *    field bound): the leaflet surface on top of plain DVR, behind CUSP_FIELD_ON. The vascular
 *    shell and its dark-evidence term are still omitted (no run ships a shell field);
 *  - the clean-root second volume (the desktop's `_CleanRootEnabled` uniform gate) is behind
 *    CLEAN_ROOT_ON: the clean volume, its per-scan TF and the root region mask, chosen per ray
 *    exactly as on the desktop; its gradient is a central difference of the clean volume
 *    (the desktop bakes Sobel gradients for the clean bundle);
 *  - `smoothstep(e, e, x)` is undefined in GLSL, so a (0, 0) lighting threshold becomes a step.
 */

export const MAX_CROSS_SECTIONS = 8;

export const dvrVertexShader = /* glsl */ `
out vec2 vUv;
out vec3 vLocal;

void main() {
  vUv = uv;
  vLocal = position;              // unit cube, ±0.5
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const dvrFragmentShader = /* glsl */ `
precision highp float;
precision highp sampler3D;

in vec2 vUv;
in vec3 vLocal;
out vec4 fragColor;

uniform mat4 uModelMatrix;        // cube local -> world (modelMatrix is vertex-only in three.js)
uniform sampler3D uData;
uniform sampler2D uTF;
uniform sampler2D uNoise;
uniform vec3 uTextureSize;
uniform vec3 uCameraLocal;        // camera position in cube-local space (±0.5 frame)
uniform vec3 uCameraForwardLocal; // camera forward direction in cube-local space (ortho)
uniform float uOrtho;             // 1 = orthographic camera
uniform float uMinVal;
uniform float uMaxVal;
uniform float uBoxOpacity;
uniform float uSamplingMultiplier;
uniform float uMinGradient;
uniform float uGradientOpacityModulation;
uniform float uLightStart;
uniform float uLightEnd;
uniform float uCameraClipDistance; // world units (1 = 100 mm)
uniform float uDepthTintIntensity;
uniform float uDepthTintDistance;
uniform vec3 uDepthTintNear;
uniform vec3 uDepthTintFar;
uniform int uCrossCount;
uniform mat4 uCrossMat[${MAX_CROSS_SECTIONS}];
uniform float uCrossType[${MAX_CROSS_SECTIONS}];
uniform float uMaskEnabled;
uniform sampler3D uMask;
uniform float uMaskThreshold;
// Cusp compartment field (_VascularCuspTex): rgb = per-cusp unsigned distance to the leaflet
// surface (0..1 of uCuspDistMaxMm), a = coaptation-deficiency mask. Density-INDEPENDENT surface
// term: the leaflet belly sits at pool density, so only geometry can find it.
uniform sampler3D uCusp;
uniform sampler3D uCuspFull;      // R8 complete geometric leaflet (_VascularCuspFullTex)
uniform float uCuspFullEnabled;
uniform float uCuspDistMaxMm;
uniform float uCuspT0;            // surface ramp start / end (mm)
uniform float uCuspT1;
uniform vec4 uCuspPlane;          // commissural cap cutoff: xyz = n (mm) scaled by the extent, w = offset mm
uniform vec3 uCuspTexelSize;      // 1 / field dims
uniform float uLeafletFill;       // 0 none .. 1 evidence surface .. 2 complete leaflet
uniform float uLeafletEmphasis;
uniform float uLeafletOpacity;    // _LeafletSetOpacity
uniform float uLeafletRefDensity; // TF axis value the leaflet colour is read from
uniform vec3 uLeafletColor;       // override (linear), mixed in by uLeafletColorMix
uniform float uLeafletColorMix;
// Clean root mode (CLEAN_ROOT_ON): a second volume (the pipeline's clean render volume, blood
// hidden inside the root) with its own per-scan transfer function, and the aortic root REGION
// mask (lumen + LVOT + orifice + wall band). The choice is made PER RAY: a ray entering through
// the volume's own boundary (an outside view) renders the bound data / TF the whole way, exactly
// the Cardiac VR look, blood-filled root included. A ray the camera clip or the cutout box
// opened renders the raw set too until it meets HIDDEN blood (clean density 0) inside the
// region (the cut face has entered the emptied lumen), then the clean volume + clean TF until it
// leaves the region. So the look switches exactly where the clip plane enters the root.
uniform sampler3D uClean;         // _CleanDataTex
uniform sampler2D uCleanTF;       // _CleanTfTex
uniform sampler3D uRegion;        // _RegionMaskTex (R8, > 0.5 inside)
uniform float uCleanLightStart;   // _CleanLightingThresholdStart / End
uniform float uCleanLightEnd;
// Per-set opacity multipliers (the clean-root tile's transparency sliders): the TF-rendered
// term is scaled by the raw / clean value according to the ray's set, the leaflet surface term
// by uLeafletOpacity.
uniform float uRawSetOpacity;
uniform float uCleanSetOpacity;

#define AMBIENT_LIGHTING_FACTOR 0.2
#define JITTER_FACTOR 5.0
#define OPACITY_THRESHOLD (1.0 - 1.0 / 255.0)

// ---------------------------------------------------------------- sampling
float sampleTrilinear(vec3 p) {
  return texture(uData, p).r;
}

// Tricubic (Ruijters), 8 trilinear taps; vol is the CT or (clean root) the clean volume.
float sampleTricubicOf(sampler3D vol, vec3 texCoord) {
  vec3 coord_grid = texCoord * uTextureSize - 0.5;
  vec3 index = floor(coord_grid);
  vec3 fraction = coord_grid - index;
  vec3 one_frac = 1.0 - fraction;
  vec3 w0 = 1.0 / 6.0 * one_frac * one_frac * one_frac;
  vec3 w1 = 2.0 / 3.0 - 0.5 * fraction * fraction * (2.0 - fraction);
  vec3 w2 = 2.0 / 3.0 - 0.5 * one_frac * one_frac * (2.0 - one_frac);
  vec3 w3 = 1.0 / 6.0 * fraction * fraction * fraction;
  vec3 g0 = w0 + w1;
  vec3 g1 = w2 + w3;
  vec3 mult = 1.0 / uTextureSize;
  vec3 h0 = mult * ((w1 / g0) - 0.5 + index);
  vec3 h1 = mult * ((w3 / g1) + 1.5 + index);
  float tex000 = texture(vol, h0).r;
  float tex100 = texture(vol, vec3(h1.x, h0.y, h0.z)).r;
  tex000 = mix(tex100, tex000, g0.x);
  float tex010 = texture(vol, vec3(h0.x, h1.y, h0.z)).r;
  float tex110 = texture(vol, vec3(h1.x, h1.y, h0.z)).r;
  tex010 = mix(tex110, tex010, g0.x);
  tex000 = mix(tex010, tex000, g0.y);
  float tex001 = texture(vol, vec3(h0.x, h0.y, h1.z)).r;
  float tex101 = texture(vol, vec3(h1.x, h0.y, h1.z)).r;
  tex001 = mix(tex101, tex001, g0.x);
  float tex011 = texture(vol, vec3(h0.x, h1.y, h1.z)).r;
  float tex111 = texture(vol, h1).r;
  tex011 = mix(tex111, tex011, g0.x);
  tex001 = mix(tex011, tex001, g0.y);
  return mix(tex001, tex000, g0.z);
}

float sampleTricubic(vec3 texCoord) {
  return sampleTricubicOf(uData, texCoord);
}

float getDensity(vec3 p) {
#ifdef CUBIC_INTERPOLATION_ON
  return sampleTricubic(p);
#else
  return sampleTrilinear(p);
#endif
}

float getDensityNoTricubic(vec3 p) {
  return sampleTrilinear(p);
}

// Central difference per voxel step (VolumeDataset CentralDifference, range 1): points toward
// decreasing density; the sign is irrelevant because lighting flips the normal toward the eye.
vec3 gradientOf(sampler3D vol, vec3 p) {
  vec3 d = 1.0 / uTextureSize;
  return vec3(
    texture(vol, p - vec3(d.x, 0.0, 0.0)).r - texture(vol, p + vec3(d.x, 0.0, 0.0)).r,
    texture(vol, p - vec3(0.0, d.y, 0.0)).r - texture(vol, p + vec3(0.0, d.y, 0.0)).r,
    texture(vol, p - vec3(0.0, 0.0, d.z)).r - texture(vol, p + vec3(0.0, 0.0, d.z)).r);
}

vec3 getGradient(vec3 p) {
  return gradientOf(uData, p);
}

vec4 getTF1DColour(float density) {
  return texture(uTF, vec2(clamp(density, 0.0, 1.0), 0.5));
}

// ---------------------------------------------------------------- clean root
#ifdef CLEAN_ROOT_ON
float getCleanDensity(vec3 p) {
#ifdef CUBIC_INTERPOLATION_ON
  return sampleTricubicOf(uClean, p);
#else
  return texture(uClean, p).r;
#endif
}

float getCleanDensityNoTricubic(vec3 p) {
  return texture(uClean, p).r;
}

vec3 getCleanGradient(vec3 p) {
  return gradientOf(uClean, p);
}

vec4 getCleanTF1DColour(float density) {
  return texture(uCleanTF, vec2(clamp(density, 0.0, 1.0), 0.5));
}

bool isInsideRootRegion(vec3 p) {
  return texture(uRegion, p).r > 0.5;
}

// Denoised density above which a cut face counts as root tissue (wall, leaflet, calcium, kept
// pool rim) rather than the fat outside the wall: -20 HU on the 0..1 scale of -1024..3071 HU.
#define CLEAN_ROOT_TISSUE_MIN (1004.0 / 4095.0)

// How much of the raw density the clean volume dropped here: 1 in hidden blood (clean written
// at the lowest level, raw at the pool level), 0 in kept tissue (clean = denoised raw) and in
// air (nothing to drop). The trilinear fetches make this a smooth field across the pool
// boundary, so the look blends over ~a voxel instead of stepping voxel by voxel.
float cleanRootHiddenness(float cleanDensity, float rawDensity) {
  float drop = (rawDensity - cleanDensity) / max(rawDensity, 4.0 / 255.0);
  return smoothstep(0.35, 0.65, drop);
}

// The blended TF colour for a clean-root mix (0 raw set, 1 clean set).
vec4 cleanRootTfColour(float rawDensity, float cleanDensity, float m) {
  if (m <= 0.0) return getTF1DColour(rawDensity);
  if (m >= 1.0) return getCleanTF1DColour(cleanDensity);
  return mix(getTF1DColour(rawDensity), getCleanTF1DColour(cleanDensity), m);
}
#endif

// ---------------------------------------------------------------- cutouts
#define CROSS_SECTION_TYPE_PLANE 1
#define CROSS_SECTION_TYPE_BOX_INCL 2
#define CROSS_SECTION_TYPE_BOX_EXCL 3
#define CROSS_SECTION_TYPE_SPHERE_INCL 4
#define CROSS_SECTION_TYPE_SPHERE_EXCL 5

bool IsCutout(vec3 currPos) {
#ifdef CROSS_SECTION_ON
  vec4 pivotPos = vec4(currPos - vec3(0.5), 1.0);
  bool clipped = false;
  bool hasSphereInclusive = false;
  bool insideAnySphereInclusive = false;
  for (int i = 0; i < ${MAX_CROSS_SECTIONS}; ++i) {
    if (i >= uCrossCount) break;
    int type = int(uCrossType[i]);
    vec3 p = (uCrossMat[i] * pivotPos).xyz;
    if (type == CROSS_SECTION_TYPE_SPHERE_INCL) {
      hasSphereInclusive = true;
      if (length(p) <= 0.5) insideAnySphereInclusive = true;
      continue;
    }
    if (clipped) continue;
    if (type == CROSS_SECTION_TYPE_PLANE) clipped = p.z > 0.0;
    else if (type == CROSS_SECTION_TYPE_BOX_INCL)
      clipped = !(p.x >= -0.5 && p.x <= 0.5 && p.y >= -0.5 && p.y <= 0.5 && p.z >= -0.5 && p.z <= 0.5);
    else if (type == CROSS_SECTION_TYPE_BOX_EXCL)
      clipped = p.x >= -0.5 && p.x <= 0.5 && p.y >= -0.5 && p.y <= 0.5 && p.z >= -0.5 && p.z <= 0.5;
    else if (type == CROSS_SECTION_TYPE_SPHERE_EXCL) clipped = length(p) < 0.5;
  }
  if (hasSphereInclusive && !insideAnySphereInclusive) return true;
  return clipped;
#else
  return false;
#endif
}

bool IsOutsideAortaMask(vec3 currPos) {
  if (uMaskEnabled < 0.5) return false;
  return texture(uMask, currPos).r < uMaskThreshold;
}

// ---------------------------------------------------------------- leaflet overlay
#ifdef CUSP_FIELD_ON
// Nearest leaflet-boundary distance (0..1 of uCuspDistMaxMm).
float cuspMinDistance(vec3 pos) {
  vec4 f = texture(uCusp, pos);
  return min(f.r, min(f.g, f.b));
}

// Per-texel central differences of that distance.
vec3 cuspMinGradient(vec3 pos) {
  vec3 d = uCuspTexelSize;
  return vec3(
    cuspMinDistance(pos + vec3(d.x, 0.0, 0.0)) - cuspMinDistance(pos - vec3(d.x, 0.0, 0.0)),
    cuspMinDistance(pos + vec3(0.0, d.y, 0.0)) - cuspMinDistance(pos - vec3(0.0, d.y, 0.0)),
    cuspMinDistance(pos + vec3(0.0, 0.0, d.z)) - cuspMinDistance(pos - vec3(0.0, 0.0, d.z)));
}

// A gradient shorter than this (per texel) is the crease itself, not a slope.
#define CUSP_SHEET_NORMAL_MIN_SQ (0.05 * 0.05)

// The leaflet sheet's normal at a sample (unnormalised; ~0 when it cannot be told). The cusp
// field is an UNSIGNED distance, a V across the sheet whose gradient vanishes exactly where the
// surface term peaks, so the slope is read one field texel on either side of the sample along
// the ray and the two sides are summed into one smooth normal. The sign is irrelevant: the
// lighting flips normals toward the eye.
vec3 cuspSheetNormal(vec3 pos, vec3 rayDir) {
  vec3 off = rayDir * uCuspTexelSize;
  vec3 g1 = cuspMinGradient(pos + off);
  vec3 g2 = cuspMinGradient(pos - off);
  if (dot(g1, g2) < 0.0) g2 = -g2;
  bool ok1 = dot(g1, g1) >= CUSP_SHEET_NORMAL_MIN_SQ;
  bool ok2 = dot(g2, g2) >= CUSP_SHEET_NORMAL_MIN_SQ;
  if (ok1 && ok2) return g1 + g2;
  return dot(g1, g1) >= dot(g2, g2) ? g1 : g2;
}

// The leaflet-surface factor at a sample: the ramp over the nearest boundary distance, the
// "leaflet fill" blend toward the complete geometric leaflet, perforated by the regurgitant gap
// (deficiency mask) and cut off above the commissural plane (the compartment's superior
// blood-blood cap is not tissue).
float cuspSurface(vec3 pos) {
  vec4 cuspField = texture(uCusp, pos);
  float defHole = 1.0 - smoothstep(0.2, 0.8, cuspField.a);
  float planeMm = dot(uCuspPlane.xyz, pos) - uCuspPlane.w;
  float planeCut = 1.0 - smoothstep(0.0, 1.5, planeMm);
  float uMinMm = min(cuspField.r, min(cuspField.g, cuspField.b)) * uCuspDistMaxMm;
  float cuspSurf = 1.0 - smoothstep(uCuspT0, uCuspT1, uMinMm);
  if (uLeafletFill <= 1.0) {
    cuspSurf *= uLeafletFill;
  } else if (uCuspFullEnabled > 0.5) {
    float fullMm = texture(uCuspFull, pos).r * uCuspDistMaxMm;
    float fullSurf = 1.0 - smoothstep(uCuspT0, uCuspT1, fullMm);
    cuspSurf = max(cuspSurf, fullSurf * (uLeafletFill - 1.0));
  }
  cuspSurf *= defHole * planeCut;
  return cuspSurf < 0.02 ? 0.0 : cuspSurf;
}
#endif

// ---------------------------------------------------------------- rays
struct RayInfo {
  vec3 startPos;
  vec3 endPos;
  vec3 direction;
  vec2 aabbInters;
};

struct RaymarchInfo {
  int numSteps;
  float numStepsRecip;
  float stepSize;
};

vec3 getViewRayDir(vec3 vertexLocal) {
  if (uOrtho < 0.5) {
    return normalize(uCameraLocal - vertexLocal);   // toward the camera (ObjSpaceViewDir)
  }
  return normalize(-uCameraForwardLocal);
}

vec2 intersectAABB(vec3 rayOrigin, vec3 rayDir, vec3 boxMin, vec3 boxMax) {
  vec3 tMin = (boxMin - rayOrigin) / rayDir;
  vec3 tMax = (boxMax - rayOrigin) / rayDir;
  vec3 t1 = min(tMin, tMax);
  vec3 t2 = max(tMin, tMax);
  float tNear = max(max(t1.x, t1.y), t1.z);
  float tFar = min(min(t2.x, t2.y), t2.z);
  return vec2(tNear, tFar);
}

RayInfo getRayBack2Front(vec3 vertexLocal) {
  RayInfo ray;
  ray.direction = getViewRayDir(vertexLocal);
  ray.startPos = vertexLocal + vec3(0.5);
  ray.aabbInters = intersectAABB(ray.startPos, ray.direction, vec3(0.0), vec3(1.0));
  if (uOrtho < 0.5) {
    float viewDist = length(uCameraLocal - vertexLocal);
    if (ray.aabbInters.y > viewDist) ray.aabbInters.y = viewDist;   // camera inside the box
  }
  ray.endPos = ray.startPos + ray.direction * ray.aabbInters.y;
  return ray;
}

RayInfo getRayFront2Back(vec3 vertexLocal) {
  RayInfo ray = getRayBack2Front(vertexLocal);
  ray.direction = -ray.direction;
  vec3 tmp = ray.startPos;
  ray.startPos = ray.endPos;
  ray.endPos = tmp;
  return ray;
}

RaymarchInfo initRaymarch(RayInfo ray, int maxNumSteps) {
  RaymarchInfo info;
  info.stepSize = 1.732 / float(maxNumSteps);
  info.numSteps = int(clamp(abs(ray.aabbInters.x - ray.aabbInters.y) / info.stepSize, 1.0, float(maxNumSteps)));
  info.numStepsRecip = 1.0 / float(info.numSteps);
  return info;
}

// ---------------------------------------------------------------- shading
vec3 calculateLighting(vec3 col, vec3 normal, vec3 lightDir, vec3 eyeDir, float specularIntensity) {
  normal *= (step(0.0, dot(normal, eyeDir)) * 2.0 - 1.0);
  float ndotl = max(dot(normal, lightDir), 0.0);
  vec3 diffuse = ndotl * col;
  vec3 ambient = AMBIENT_LIGHTING_FACTOR * col;
  vec3 r = normalize(reflect(-lightDir, normal));
  float rdotv = max(dot(r, eyeDir), 0.0);
  vec3 specular = pow(rdotv, 32.0) * vec3(1.0) * specularIntensity;
  return min(diffuse + ambient + specular, vec3(1.0));
}

float lightingFactor(float gradMag) {
  // smoothstep(e, e, x) is undefined in GLSL; the desktop's (0, 0) threshold means "lit
  // wherever there is any gradient".
  if (uLightEnd > uLightStart) return smoothstep(uLightStart, uLightEnd, gradMag);
  return gradMag > uLightStart ? 1.0 : 0.0;
}

vec3 worldOf(vec3 pos01) {
  return (uModelMatrix * vec4(pos01 - vec3(0.5), 1.0)).xyz;
}

bool shouldClipNear(vec3 pos01) {
  if (uCameraClipDistance <= 0.0) return false;
  return distance(cameraPosition, worldOf(pos01)) < uCameraClipDistance;
}

vec3 applyDepthTint(vec3 rgb, vec3 pos01) {
  if (uDepthTintIntensity <= 0.0) return rgb;
  float dist = distance(cameraPosition, worldOf(pos01));
  float t = clamp(dist / max(uDepthTintDistance, 0.0001), 0.0, 1.0);
  vec3 depthCol = mix(uDepthTintNear, uDepthTintFar, t);
  return rgb * mix(vec3(1.0), depthCol, clamp(uDepthTintIntensity, 0.0, 1.0));
}

// ---------------------------------------------------------------- DVR
vec4 frag_dvr() {
  const int MAX_NUM_STEPS = 256;
  int samplingRate = int(float(MAX_NUM_STEPS) * uSamplingMultiplier);
  RayInfo ray = getRayFront2Back(vLocal);
  RaymarchInfo rm = initRaymarch(ray, samplingRate);

  ray.startPos += (JITTER_FACTOR * ray.direction * rm.stepSize) * texture(uNoise, vUv).r;

  vec4 col = vec4(0.0);
  float blendFactor = 1.0 / uSamplingMultiplier;
  // Clean root mode. cleanMix = 0 renders the raw set, 1 the clean set, in between a blend.
  // Only a ray whose CUT FACE is the camera clip / cutout box itself, lying inside the root
  // region AND in the root's wall / leaflet tissue or its emptied pool, is eligible at all.
  // Every other ray (entering through the volume's own boundary or the isolation crop: a mask
  // skip cancels an earlier clip skip; or cut in the air / fat in front of the wall) renders
  // the raw set throughout, blood glowing through the wall included, so the wall reads opaque
  // exactly as in the Cardiac VR preset until the plane is inside it. An eligible ray rises to
  // the clean set (sticky max) ONLY at hidden blood, never on tissue, so the transition sits
  // where the clip plane enters the emptied lumen. Leaving the region drops back to the raw
  // set. The driver is a smooth field (trilinear fetches), which keeps the cut edge from
  // stair-stepping per voxel.
  bool rayCut = false;
  bool rayStarted = false;
  bool rayEligible = false;
  float cleanMix = 0.0;
  for (int iStep = 0; iStep < 2048; iStep++) {
    if (iStep >= rm.numSteps) break;
    float t = float(iStep) * rm.numStepsRecip;
    vec3 currPos = mix(ray.startPos, ray.endPos, t);

    if (shouldClipNear(currPos)) { rayCut = true; continue; }
    if (IsCutout(currPos)) { rayCut = true; continue; }
    if (IsOutsideAortaMask(currPos)) { rayCut = false; continue; }

#ifdef CLEAN_ROOT_ON
    if (rayCut && (rayEligible || !rayStarted)) {
      bool inRegion = isInsideRootRegion(currPos);
      if (!rayStarted) {
        float cleanFace = getCleanDensityNoTricubic(currPos);
        rayEligible = inRegion
          && (cleanFace > CLEAN_ROOT_TISSUE_MIN
              || cleanRootHiddenness(cleanFace, getDensityNoTricubic(currPos)) > 0.0);
      }
      if (!rayEligible || !inRegion) {
        cleanMix = 0.0;
      } else if (cleanMix < 1.0) {
        cleanMix = max(cleanMix, cleanRootHiddenness(getCleanDensityNoTricubic(currPos), getDensityNoTricubic(currPos)));
      }
    }
#endif
    rayStarted = true;

    // Leaflet surface (v15 overlay): a sample carrying it must reach the compositor even when
    // the TF / window would cull it (the Leaflets preset's TF is fully transparent by design).
    // In clean-root mode only on rays rendering the clean set, so an outside view stays the
    // plain Cardiac VR look.
    float cuspSurf = 0.0;
#ifdef CUSP_FIELD_ON
#ifdef CLEAN_ROOT_ON
    if (cleanMix > 0.5) cuspSurf = cuspSurface(currPos);
#else
    cuspSurf = cuspSurface(currPos);
#endif
#endif

#ifdef CLEAN_ROOT_ON
#ifdef CUBIC_INTERPOLATION_ON
    if (cuspSurf == 0.0
        && cleanRootTfColour(cleanMix < 1.0 ? getDensityNoTricubic(currPos) : 0.0,
                             cleanMix > 0.0 ? getCleanDensityNoTricubic(currPos) : 0.0,
                             cleanMix).a == 0.0) continue;
#endif
    // The sample value of the current position (raw set, clean set, or both for a blended
    // clean-root sample).
    float rawDensity = cleanMix < 1.0 ? getDensity(currPos) : 0.0;
    float cleanDensity = cleanMix > 0.0 ? getCleanDensity(currPos) : 0.0;
    float density = mix(rawDensity, cleanDensity, cleanMix);
    bool inWindow = density >= uMinVal && density <= uMaxVal;
    if (cuspSurf == 0.0 && !inWindow) continue;
    vec4 src = cleanRootTfColour(rawDensity, cleanDensity, cleanMix);
#else
#ifdef CUBIC_INTERPOLATION_ON
    if (cuspSurf == 0.0 && getTF1DColour(getDensityNoTricubic(currPos)).a == 0.0) continue;
#endif
    float density = getDensity(currPos);
    bool inWindow = density >= uMinVal && density <= uMaxVal;
    if (cuspSurf == 0.0 && !inWindow) continue;
    vec4 src = getTF1DColour(density);
#endif
    if (cuspSurf == 0.0 && src.a == 0.0) continue;

    // Combine the TF term with the leaflet surface: max() never double-counts, and the
    // contribution-ratio colour blend is seam-free where the leaflet meets its attachment.
    float bandA = src.a * (inWindow ? 1.0 : 0.0);
#ifdef CLEAN_ROOT_ON
    // Per-set transparency (clean-root tile): the TF-rendered term follows the set the ray
    // renders, the leaflet term its own.
    bandA *= mix(uRawSetOpacity, uCleanSetOpacity, cleanMix);
#endif
    float leafletW = 0.0;   // leaflet share of the sample's colour (and lighting normal)
    src.a = bandA;
#ifdef CUSP_FIELD_ON
    if (cuspSurf > 0.0) {
      float softA = cuspSurf * uLeafletEmphasis * uLeafletOpacity;
      leafletW = softA / max(bandA + softA, 1e-5);
      // The leaflet colour is the rendered set's own tissue colour at the reference density (the
      // clean TF on clean-root rays, the bound TF otherwise), so wall and membrane meet without
      // a seam; the preset may override it (colour mix 1).
#ifdef CLEAN_ROOT_ON
      vec3 leafletTissue = cleanMix > 0.5 ? getCleanTF1DColour(uLeafletRefDensity).rgb : getTF1DColour(uLeafletRefDensity).rgb;
#else
      vec3 leafletTissue = getTF1DColour(uLeafletRefDensity).rgb;
#endif
      vec3 leafletRgb = mix(leafletTissue, uLeafletColor, uLeafletColorMix);
      src.rgb = mix(src.rgb, leafletRgb, leafletW);
      src.a = max(bandA, softA);
    }
#endif
    if (src.a == 0.0) continue;

#ifdef LIGHTING_ON
#ifdef CLEAN_ROOT_ON
    vec3 gradient = cleanMix <= 0.0 ? getGradient(currPos)
                  : (cleanMix >= 1.0 ? getCleanGradient(currPos)
                  : mix(getGradient(currPos), getCleanGradient(currPos), cleanMix));
#else
    vec3 gradient = getGradient(currPos);
#endif
    float gradMag = length(gradient);
    if (uGradientOpacityModulation > 0.0) {
      float gradEdgeEnd = max(uMinGradient, 0.0001);
      float boundaryFactor = smoothstep(gradEdgeEnd * 0.4, gradEdgeEnd, gradMag);
      src.a *= mix(1.0, boundaryFactor, uGradientOpacityModulation);
      if (src.a == 0.0) continue;
    }
#ifdef CLEAN_ROOT_ON
    float lightStart = mix(uLightStart, uCleanLightStart, cleanMix);
    float lightEnd = mix(uLightEnd, uCleanLightEnd, cleanMix);
    float factor = lightEnd > lightStart ? smoothstep(lightStart, lightEnd, gradMag) : (gradMag > lightStart ? 1.0 : 0.0);
#else
    float factor = lightingFactor(gradMag);
#endif
    vec3 normal = gradMag > 1e-6 ? gradient / gradMag : vec3(0.0);
    vec3 eyeDir = -ray.direction;
#ifdef CUSP_FIELD_ON
    if (leafletW > 0.0) {
      // The leaflet's share is shaded from the sheet's own geometry and always fully lit, like
      // the wall it attaches to: the CT gradient at the belly is pool noise. Both normals are
      // turned toward the eye before blending so opposite signs cannot cancel across the seam.
      vec3 cuspNormal = cuspSheetNormal(currPos, ray.direction);
      float sheetMag = length(cuspNormal);
      if (sheetMag > 1e-6) {
        vec3 sheetN = cuspNormal / sheetMag;
        sheetN *= (step(0.0, dot(sheetN, eyeDir)) * 2.0 - 1.0);
        vec3 volN = normal * (step(0.0, dot(normal, eyeDir)) * 2.0 - 1.0);
        vec3 mixedN = mix(volN * factor, sheetN, leafletW);
        float mixedMag = length(mixedN);
        normal = mixedMag > 1e-6 ? mixedN / mixedMag : sheetN;
        factor = mix(factor, 1.0, leafletW);
      }
    }
#endif
    vec3 shaded = dot(normal, normal) > 0.5
      ? calculateLighting(src.rgb, normal, eyeDir, eyeDir, 0.3)
      : src.rgb;
    src.rgb = mix(src.rgb, shaded, factor);
#endif

    src.rgb = applyDepthTint(src.rgb, currPos);

    src.a = 1.0 - pow(1.0 - src.a, blendFactor);
    src.a *= uBoxOpacity;
    src.rgb *= src.a;
    col = (1.0 - col.a) * src + col;

    if (col.a > OPACITY_THRESHOLD) break;
  }
  return col;
}

// ---------------------------------------------------------------- MIP
vec4 frag_mip() {
  const int MAX_NUM_STEPS = 256;
  int samplingRate = int(float(MAX_NUM_STEPS) * uSamplingMultiplier);
  RayInfo ray = getRayBack2Front(vLocal);
  RaymarchInfo rm = initRaymarch(ray, samplingRate);
  float maxDensity = 0.0;
  vec3 maxDensityPos = ray.startPos;
  for (int iStep = 0; iStep < 2048; iStep++) {
    if (iStep >= rm.numSteps) break;
    float t = float(iStep) * rm.numStepsRecip;
    vec3 currPos = mix(ray.startPos, ray.endPos, t);
    if (shouldClipNear(currPos)) continue;
    if (IsCutout(currPos)) continue;
    if (IsOutsideAortaMask(currPos)) continue;
    float density = getDensity(currPos);
    if (density > maxDensity && density > uMinVal && density < uMaxVal) {
      maxDensity = density;
      maxDensityPos = currPos;
    }
  }
  float win = clamp((maxDensity - uMinVal) / max(uMaxVal - uMinVal, 1e-4), 0.0, 1.0);
  vec4 outc = vec4(1.0, 1.0, 1.0, win * uBoxOpacity);
  outc.rgb = applyDepthTint(outc.rgb, maxDensityPos);
  return outc;
}

// ---------------------------------------------------------------- ISO
vec4 frag_surf() {
  const int MAX_NUM_STEPS = 512;
  int samplingRate = int(float(MAX_NUM_STEPS) * uSamplingMultiplier);
  RayInfo ray = getRayFront2Back(vLocal);
  RaymarchInfo rm = initRaymarch(ray, samplingRate);
  ray.startPos += (JITTER_FACTOR * ray.direction * rm.stepSize) * texture(uNoise, vUv).r;
  vec4 col = vec4(0.0);
  vec3 hitPos01 = ray.endPos;
  for (int iStep = 0; iStep < 4096; iStep++) {
    if (iStep >= rm.numSteps) break;
    float t = float(iStep) * rm.numStepsRecip;
    vec3 currPos = mix(ray.startPos, ray.endPos, t);
    if (shouldClipNear(currPos)) continue;
    if (IsCutout(currPos)) continue;
    if (IsOutsideAortaMask(currPos)) continue;
    float density = getDensity(currPos);
    if (density > uMinVal && density < uMaxVal) {
      vec3 gradient = getGradient(currPos);
      float gradMag = length(gradient);
      if (gradMag > uMinGradient) {
        vec3 normal = gradient / gradMag;
        col = getTF1DColour(density);
        vec3 eyeDir = -ray.direction;
        col.rgb = calculateLighting(col.rgb, normal, eyeDir, eyeDir, 0.15);
        col.a = 1.0;
        hitPos01 = currPos;
        break;
      }
    }
  }
  col *= uBoxOpacity;
  col.rgb = applyDepthTint(col.rgb, hitPos01);
  return col;
}

void main() {
#if defined(MODE_MIP)
  vec4 c = frag_mip();
#elif defined(MODE_SURF)
  vec4 c = frag_surf();
#else
  vec4 c = frag_dvr();
#endif
  fragColor = linearToOutputTexel(c);
}
`;
